/**
 * IOC Blocklist hooks.
 *
 * Wire contract: see `LOVABLE_TODO.md` (2026-06-06).
 *   GET    /admin/iocs
 *   POST   /admin/iocs
 *   POST   /admin/iocs/bulk
 *   GET    /admin/iocs/:id
 *   DELETE /admin/iocs/:id
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { edrDelete, edrGet, edrPost, EdrApiError } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  CreateIocInput,
  IocBulkResult,
  IocKind,
  IocRecord,
  IocSeverity,
} from "@/types/edr-ioc";

const BASE = "/api/v1/connectors/edr/admin/iocs";

export interface IocsParams {
  kind?: IocKind;
  severity?: IocSeverity;
  limit?: number;
}

export function useIocs(params: IocsParams = {}) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    queryKey: ["edr", "iocs", params],
    enabled,
    queryFn: async () => {
      try {
        const res = await edrGet<{ count: number; iocs?: IocRecord[]; items?: IocRecord[] }>(
          BASE,
          {
            params: {
              kind: params.kind,
              severity: params.severity,
              limit: params.limit ?? 100,
            },
          },
        );
        return res.iocs ?? res.items ?? [];
      } catch (e) {
        if (e instanceof EdrApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}

export function useCreateIoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateIocInput) => edrPost<IocRecord>(BASE, input),
    onSuccess: () => {
      toast.success("IOC added to blocklist");
      qc.invalidateQueries({ queryKey: ["edr", "iocs"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not add IOC"),
  });
}

export function useBulkIocs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: CreateIocInput[]) =>
      edrPost<IocBulkResult>(`${BASE}/bulk`, { items }),
    onSuccess: (res) => {
      const ok = res.results.filter((r) => r.ok).length;
      const fail = res.results.length - ok;
      toast.success(`Imported ${ok} IOC${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
      qc.invalidateQueries({ queryKey: ["edr", "iocs"] });
    },
    onError: (e: Error) => toast.error(e.message || "Bulk import failed"),
  });
}

export function useDeleteIoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => edrDelete<void>(`${BASE}/${id}`),
    onSuccess: () => {
      toast.success("IOC removed");
      qc.invalidateQueries({ queryKey: ["edr", "iocs"] });
    },
    onError: (e: Error) => toast.error(e.message || "Remove failed"),
  });
}
